<template>
  <component
    :is="layoutComponent"
    page-title="Well Head Sheets"
    page-subtitle="24-hour well test field sheets"
  >
    <div class="space-y-6 pt-4">
      <div class="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
        <div class="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <h2 class="text-xl font-semibold text-white">Sheets</h2>
          <button
            class="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            :disabled="creating"
            @click="createSheet"
          >
            {{ creating ? 'Creating…' : 'New Well Head Sheet' }}
          </button>
        </div>

        <p v-if="error" class="text-red-400 mb-4">{{ error }}</p>
        <p v-else-if="loading" class="text-slate-400">Loading…</p>

        <div v-else-if="!sheets.length" class="text-slate-400 py-10 text-center">
          No well head sheets yet. Create one to start a 24-hour well test.
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full text-sm text-left">
            <thead class="text-slate-400 border-b border-slate-700">
              <tr>
                <th class="py-2 pr-4 font-medium">Title</th>
                <th class="py-2 pr-4 font-medium">Report Date</th>
                <th class="py-2 pr-4 font-medium">Customer</th>
                <th class="py-2 pr-4 font-medium">Well Name</th>
                <th class="py-2 pr-4 font-medium">L.S.D.</th>
                <th class="py-2 pr-4 font-medium">Rows Filled</th>
                <th class="py-2 pr-4 font-medium">Status</th>
                <th class="py-2 pr-4 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="sheet in sheets"
                :key="sheet.id"
                class="border-b border-slate-800/80 text-slate-200 hover:bg-slate-700/20 cursor-pointer"
                @click="openSheet(sheet.id)"
              >
                <td class="py-3 pr-4 font-medium text-white">{{ sheet.title }}</td>
                <td class="py-3 pr-4">{{ sheet.report_date || '—' }}</td>
                <td class="py-3 pr-4">{{ sheet.customer || '—' }}</td>
                <td class="py-3 pr-4">{{ sheet.well_name || '—' }}</td>
                <td class="py-3 pr-4">{{ sheet.lsd || '—' }}</td>
                <td class="py-3 pr-4">{{ sheet.row_count }} / 24</td>
                <td class="py-3 pr-4">
                  <span
                    class="px-2 py-1 rounded-full text-xs"
                    :class="sheet.status === 'active'
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-slate-500/20 text-slate-400'"
                  >{{ sheet.status }}</span>
                </td>
                <td class="py-3 pr-4">
                  <div class="flex items-center gap-3">
                    <span class="text-emerald-400">Open</span>
                    <button
                      class="text-red-400 hover:text-red-300 disabled:opacity-50"
                      :disabled="deletingId === sheet.id"
                      @click.stop="deleteSheet(sheet)"
                    >
                      {{ deletingId === sheet.id ? 'Deleting…' : 'Delete' }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import AppLayout from '@/components/layout/AppLayout.vue'

const route = useRoute()
const router = useRouter()
const isEmployee = computed(() => !route.path.startsWith('/company'))
const fieldHome = computed(() => route.path.startsWith('/employee') ? '/employee' : '')
const layoutComponent = computed(() => isEmployee.value ? AppLayout : CompanyAppLayout)
const apiBase = computed(() => isEmployee.value ? '/api/employee/well-head-sheets' : '/api/company/well-head-sheets')
const sheets = ref<Record<string, any>[]>([])
const loading = ref(false)
const creating = ref(false)
const deletingId = ref<number | null>(null)
const error = ref('')

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
    'Content-Type': 'application/json',
  }
}

async function loadSheets() {
  loading.value = true
  error.value = ''
  try {
    const res = await fetch(apiBase.value, { headers: authHeaders() })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    sheets.value = (data.sheets || []).map((sheet: any) => ({
      ...sheet,
      report_date: sheet.report_date ?? sheet.reportDate,
      well_name: sheet.well_name ?? sheet.wellName,
      row_count: sheet.row_count ?? sheet.rowCount ?? 0,
      status: sheet.status ?? 'active',
    }))
  } catch (e: any) {
    error.value = e.message || 'Failed to load well head sheets'
  } finally {
    loading.value = false
  }
}

function openSheet(id: number) {
  router.push(isEmployee.value ? `${fieldHome.value}/well-head-sheets/${id}` || `/well-head-sheets/${id}` : `/company/well-head-sheets/${id}`)
}

async function createSheet() {
  creating.value = true
  error.value = ''
  try {
    const res = await fetch(apiBase.value, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        title: `Well Head Sheet ${new Date().toLocaleDateString()}`,
        reportDate: new Date().toISOString().slice(0, 10),
      }),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    if (data.id) {
      openSheet(data.id)
      return
    }
    await loadSheets()
  } catch (e: any) {
    error.value = e.message || 'Create failed'
  } finally {
    creating.value = false
  }
}

async function deleteSheet(sheet: Record<string, any>) {
  if (!window.confirm(`Delete ${sheet.title || 'this well head sheet'}? This cannot be undone.`)) return
  deletingId.value = Number(sheet.id)
  error.value = ''
  try {
    const res = await fetch(`${apiBase.value}/${sheet.id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
    sheets.value = sheets.value.filter((item) => item.id !== sheet.id)
  } catch (e: any) {
    error.value = e.message || 'Delete failed'
  } finally {
    deletingId.value = null
  }
}

onMounted(loadSheets)
</script>
